import React, { useState, useMemo, useCallback, useContext, useEffect } from 'react';
import classNames from 'classnames';
import { isUndefined, result, isEmpty } from 'underscore';
import { Modal } from '@fluentui/react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useLock, keyVault, priorityLevel } from '@bingads-webui-react/popup-manager';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { SurveyContext } from '@bingads-webui-campaign-react/survey-context';
import { stringifySync } from '@bingads-webui/safe-json';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { SurveyConfigurator } from './survey-construction/configurator/survey';
import { SurveyConfiguration } from './survey.types';
import { SurveyView } from './survey-construction/view/survey';
import { modalStyles } from './styles';
import { returnFocus } from './utility';

interface SurveyFactoryProps {
  getSurveyConfiguration: (options: any) => SurveyConfiguration;
  onSurveySubmit: (options: any) => void;
  onSurveyDismiss: (options: any) => void;
  onSurveyRender: (options: any) => void;
  surveyDataService: {
    sendFeedback: any;
  };
  logActivity?: (string) => void;
  surveyName?: string;
  // eslint-disable-next-line react/require-default-props
  lastActiveElement?: HTMLElement;
  customParams?: any;
}

function usePage(surveyConfigurator: SurveyConfigurator, surveyAnswers: any, { setCurrentPageDirty, logActivity, analysisActivity }) {
  const [pageIndex, setPageIndex] = useState(0);
  const [fromBack, setFromBack] = useState(false);
  const [isEndingPage, setIsEndingPage] = useState(false);

  const onNext = useCallback(() => {
    const currentPage = surveyConfigurator.getPage(pageIndex);
    if (currentPage.isValid(surveyAnswers)) {
      logActivity('action: next page');
      setPageIndex(pageIndex + 1);
      analysisActivity('onNext');
      setFromBack(false);
    } else {
      setCurrentPageDirty(pageIndex);
      analysisActivity('onNext - invalid');
    }
  }, [surveyConfigurator, pageIndex, surveyAnswers, logActivity, analysisActivity, setCurrentPageDirty]);

  const onBack = useCallback(() => {
    setPageIndex(pageIndex - 1);
    logActivity('action: previous page');
    analysisActivity('onBack');
    setFromBack(true);
  }, [pageIndex, logActivity, analysisActivity]);


  const getIsFirstPage = useCallback(() => pageIndex === 0, [pageIndex]);
  const getIsLastPage = useCallback(
    () =>
      !surveyConfigurator.hasPageAtIndex(pageIndex + 1, surveyAnswers),
    [pageIndex, surveyAnswers, surveyConfigurator]
  );

  const getPageIndex = useCallback(() => pageIndex, [pageIndex]);
  const getIsEndingPage = useCallback(() => isEndingPage, [isEndingPage]);
  const getIsFromBack = useCallback(() => fromBack, [fromBack]);

  return {
    getPageIndex,
    getIsEndingPage,
    getIsFromBack,
    setIsEndingPage,
    onNext,
    onBack,
    getIsFirstPage,
    getIsLastPage,
  };
}

function useQuestionState(surveyConfigurator: SurveyConfigurator) {
  const [surveyAnswers, setSurveyAnswers] = useState(surveyConfigurator.getDefaultAnswers());
  const [surveyDirtyState, setSurveyDirtyState] = useState(surveyConfigurator.getDefaultDirtyState(false));

  const setAnswersByKey = useCallback((key, data) => {
    const newAnswers = { ...surveyAnswers };
    newAnswers[key] = data;
    setSurveyAnswers(newAnswers);
  }, [setSurveyAnswers, surveyAnswers]);

  const setSurveyDirtyStateByKey = useCallback((key, data) => {
    const newDirtyState = { ...surveyDirtyState };
    newDirtyState[key] = data;
    setSurveyDirtyState(newDirtyState);
  }, [surveyDirtyState, setSurveyDirtyState]);

  const getAnswersByKey = useCallback(key => surveyAnswers[key], [surveyAnswers]);
  const getSurveyAnswers = useCallback(() => surveyAnswers, [surveyAnswers]);
  const getSurveyDirtyStateByKey = useCallback(key => surveyDirtyState[key], [surveyDirtyState]);
  const getSurveyDirtyState = useCallback(() => surveyDirtyState, [surveyDirtyState]);

  const setCurrentPageDirty = useCallback((pageIndex) => {
    const newState = surveyConfigurator.getDefaultDirtyStatePerPage(true, pageIndex);
    setSurveyDirtyState({ ...surveyDirtyState, ...newState });
  }, [surveyConfigurator, surveyDirtyState]);

  return {
    getSurveyAnswers,
    setAnswersByKey,
    getAnswersByKey,
    getSurveyDirtyState,
    setSurveyDirtyStateByKey,
    getSurveyDirtyStateByKey,
    setCurrentPageDirty,
  };
}

function parseSurveyConfiguration({ getSurveyConfiguration, serverContext, i18n }): SurveyConfigurator {
  const surveyConfiguration = getSurveyConfiguration({
    i18n,
    serverContext,
  });
  const {
    rating,
    email,
    questions,
    featureArea,
  } = surveyConfiguration;

  const surveyConfigurator = new SurveyConfigurator({
    featureArea,
    serverContext,
    i18n,
    configuration: surveyConfiguration,
  });

  if (rating) {
    surveyConfigurator.addQuestion(rating);
  }
  if (email) {
    surveyConfigurator.addQuestion(email);
  }
  surveyConfigurator.addQuestions(questions);

  surveyConfigurator.sortPages();
  return surveyConfigurator;
}

export const SurveyFactory = (props: SurveyFactoryProps) => {
  const {
    getSurveyConfiguration,
    surveyDataService,
    onSurveySubmit,
    onSurveyDismiss,
    onSurveyRender,
    logActivity,
    surveyName,
    lastActiveElement,
    customParams,
  } = props;

  const classes: any = useDefaultStyles(() => modalStyles);

  // @ts-ignore
  const serverContext = useContext(ServerContext);
  const i18n = useContext(I18nContext) as any;
  // @ts-ignore
  const isPopupOutsideDismissEnabled = result(serverContext, ['DynamicPermissions', 'PopupOutsideDismiss'], false) || result(serverContext, ['CCUIPilots', 'dynamic', 'PopupOutsideDismiss'], false);

  const surveyConfigurator = useMemo(
    () => parseSurveyConfiguration({ getSurveyConfiguration, serverContext, i18n }),
    [getSurveyConfiguration, serverContext, i18n]
  );

  const [isModalOpen, setIsModalOpen] = useState(true);

  const {
    getSurveyAnswers,
    getAnswersByKey,
    setAnswersByKey,
    getSurveyDirtyState,
    setSurveyDirtyStateByKey,
    getSurveyDirtyStateByKey,
    setCurrentPageDirty,
  } = useQuestionState(surveyConfigurator);

  const surveyAnswers = getSurveyAnswers();
  const surveyDirtyState = getSurveyDirtyState();

  const {
    modalSettings = {},
    others = {},
  } = surveyConfigurator.getConfiguration();

  const {
    isGlobalModalStyleEnabled,
    userActionAnalysisEnabled,
    isUserInitiatedSurvey,
    isThankYouPageEnabled = true,
    shouldAutoClose = true,
    isInPageMiddle = false,
    isModeless: isOriginalModeless,
    isBlocking = false,
  } = others;

  const myLogActivity = useCallback((string: string) => {
    if (logActivity && surveyName) {
      logActivity(`SurveyFactory | survey: ${surveyName}, ${string}`);
    }
  }, [logActivity, surveyName]);

  const analysisActivity = useCallback((action: string) => {
    if (!userActionAnalysisEnabled) {
      return;
    }

    function flattenKeys(obj: object, prefix = '') {
      return Object.keys(obj).reduce((acc, key) => {
        const newKey = prefix ? `${prefix}.${key}` : key;
        if (typeof obj[key] === 'object') {
          Object.assign(acc, flattenKeys(obj[key], newKey));
        } else {
          acc[newKey] = obj[key];
        }
        return acc;
      }, {});
    }

    if (surveyName) {
      setClarityTag({ SurveyFactory: `${surveyName}, ${action}` });

      if (logActivity) {
        // @ts-ignore
        logActivity(`SurveyFactory | survey: ${surveyName}, action: ${action}, surveyAnswers: ${JSON.stringify(flattenKeys(surveyAnswers)).replaceAll('"', '')}`);
        // @ts-ignore
        logActivity(`SurveyFactory | survey: ${surveyName}, action: ${action}, surveyDirtyState: ${JSON.stringify(flattenKeys(surveyDirtyState)).replaceAll('"', '')}`);
      }
    }
  }, [userActionAnalysisEnabled, surveyName, logActivity, surveyAnswers, surveyDirtyState]);

  const additionalLogData = useMemo(() => {
    // @ts-ignore
    const userCreatedDateTime = result(serverContext, ['CurrentUser', 'FetchedCreatedDateTime']) || result(serverContext, ['CurrentUser', 'CreatedDateTime']);
    const localizedUserCreatedDateTime = new Date(userCreatedDateTime);
    const now = new Date();
    const deltaTime = now.getTime() - localizedUserCreatedDateTime.getTime();
    const deltaDay = Math.ceil(deltaTime / (1 * 24 * 60 * 60 * 1000));

    return {
      userCreatedDateTime,
      userActiveDay: deltaDay,
    };
  }, [serverContext]);

  const { lock, releaseLock } = useLock({
    key: `${keyVault.survey}-${surveyName}`,
    // Increase the WebuiUserSurvey priority to let it not be affected by teaching bubble
    priority: surveyName === 'WebuiUserSurvey' ? priorityLevel.systemLevelAnnouncement : priorityLevel.survey,
    force: isUserInitiatedSurvey,
    // @ts-ignore
    autoClose: shouldAutoClose && result(serverContext, ['Permissions', 'IsCentralizedPopupManagementEnabled']),
  });

  useEffect(() => {
    if (lock) {
      onSurveyRender?.(additionalLogData);
    }
  }, [lock, onSurveyRender, additionalLogData]);

  const {
    getPageIndex,
    onNext,
    onBack,
    getIsEndingPage,
    getIsLastPage,
    getIsFirstPage,
    setIsEndingPage,
    getIsFromBack,
  } = usePage(surveyConfigurator, surveyAnswers, { setCurrentPageDirty, logActivity: myLogActivity, analysisActivity });

  const onCloseModal = useCallback(() => {
    setIsModalOpen(false);
    returnFocus(lastActiveElement);
    releaseLock();
  }, [lastActiveElement, releaseLock]);

  const onSubmit = useCallback(() => {
    const pageIndex = getPageIndex();
    const currentPage = surveyConfigurator.getPage(pageIndex);

    if (currentPage.isValid(surveyAnswers)) {
      const { feedback, extendedManifestData } = surveyConfigurator.getFeedbackData(surveyAnswers);
      const telemetry = {
        featureArea: surveyConfigurator.getFeatureArea(),
      };

      surveyDataService.sendFeedback({
        feedback,
        extendedManifestData,
        telemetry,
        onFinishSendRequest: () => {
          myLogActivity('action: submit');
          myLogActivity(`trace: feedback, ${stringifySync(feedback)}`);
          if (customParams) {
            myLogActivity(`trace: customParams, ${stringifySync(customParams)} | trace: extendedManifestData, ${stringifySync(extendedManifestData)}`);
          } else {
            myLogActivity(`trace: extendedManifestData, ${stringifySync(extendedManifestData)}`);
          }
          // @ts-ignore
          const hasComment = !isEmpty(result(feedback, ['comment'], ''));
          onSurveySubmit({
            hasComment,
            ...additionalLogData,
          });
        },
      });

      setIsEndingPage(true);

      if (!isThankYouPageEnabled) {
        onCloseModal();
      }

      analysisActivity('onSubmit');
    } else {
      setCurrentPageDirty(pageIndex);
      analysisActivity('onSubmit - invalid');
    }
  }, [getPageIndex, surveyConfigurator, surveyAnswers, surveyDataService, setIsEndingPage, isThankYouPageEnabled, analysisActivity, myLogActivity, customParams, onSurveySubmit, onCloseModal, setCurrentPageDirty, additionalLogData]);

  const onDismiss = useCallback((dismissType: string) => {
    onCloseModal();
    onSurveyDismiss(additionalLogData);
    myLogActivity(`action: dismiss, dismissType: ${dismissType}`);
    analysisActivity(`onDismiss - ${dismissType}`);
  }, [onCloseModal, onSurveyDismiss, myLogActivity, analysisActivity, additionalLogData]);

  const onDone = useCallback(() => {
    onCloseModal();
    myLogActivity('action: done');
  }, [onCloseModal, myLogActivity]);

  const onModalDismiss = useCallback((event) => {
    if (event && event.keyCode === 27) { // Escape
      onDismiss('keyboard - ESC');
    } else {
      onCloseModal();
      myLogActivity('action: lightDismiss');
    }
  }, [onDismiss, onCloseModal, myLogActivity]);

  const modalSpecificStyles = useMemo(() => {
    if (isInPageMiddle) {
      return {
        position: 'absolute',
      };
    }

    return {
      main: {
        position: 'absolute',
        right: '40px',
        bottom: '0',
        minHeight: '0',
        zIndex: 10000000,
      },
    };
  }, [isInPageMiddle]);

  const isModeless = useMemo(() => {
    if (!isUndefined(isOriginalModeless)) {
      return isOriginalModeless;
    }

    return !isPopupOutsideDismissEnabled;
  }, [isOriginalModeless, isPopupOutsideDismissEnabled]);

  useEffect(() => {
    myLogActivity('action: mount');
  }, [myLogActivity]);

  return lock && (
    <SurveyContext.Provider
      value={{
        surveyConfigurator,
        getSurveyConfiguration: () => surveyConfigurator.getConfiguration(),
        getIsSubmitButtonDisabled: () => false,
        setSurveyDirtyStateByKey,
        getSurveyDirtyStateByKey,
        getSurveyAnswers,
        getAnswersByKey,
        setAnswersByKey,
        onNext,
        onBack,
        getPageIndex,
        getIsFirstPage,
        getIsLastPage,
        getIsEndingPage,
        setIsEndingPage,
        getIsFromBack,
        onDismiss,
        onSubmit,
        onDone,
        logActivity: myLogActivity,
      }}
    >
      <Modal
        className={classNames('webui-user-survey', isGlobalModalStyleEnabled ? classes.modalView : '')}
        isOpen={isModalOpen}
        isBlocking={isBlocking}
        onDismiss={onModalDismiss}
        isModeless={isModeless}
        titleAriaId="SurveyTitle"
        // @ts-ignore
        focusTrapZoneProps={{
          forceFocusInsideTrap: false,
        }}
        styles={{
          ...modalSpecificStyles,
          layer: {
            '& .ms-Overlay': {
              backgroundColor: 'transparent',
            },
          },
        }}
        {...modalSettings}
      >
        {isModalOpen && <SurveyView
          key={`survey-${surveyConfigurator.getFeatureArea()}`}
        />}
      </Modal>
    </SurveyContext.Provider>
  );
};

SurveyFactory.defaultProps = {
  logActivity: undefined,
  surveyName: undefined,
  customParams: undefined,
};
